import { computed, ref } from 'vue'
import type { AppMessage, BotMessage, UserMessage, BotResponse, SamGptCompletionsOptions } from './types'
import { getDomainUserId } from '@/utils/getDomainUserId'
import { useAppChatStore } from '@/stores/appChatStore'
import { useUserStore } from '@/stores/userStore'
import {
  getConversationId,
  generateMessageId,
  generateTurnId,
  generateInteractionId,
  getTimeStamp,
  parseMessage,
  updateSalesforceLead,
  extractListingIds,
  fetchRecommendations,
  shouldStopAfterScheduleMessage,
  parseSamMessageAndGetAdditionalMessages,
  transformBoldToMarkdown
} from '@/composables/useSamGptCompletions/utils'
import type { Customer } from '@/services/cdpService/types'
import { delay } from '@/utils/delay'

export const MAX_CONSECUTIVE_MESSAGES = 4

const useSamGptCompletions = ({ listingIds, listingName, enterpriseId = '' }: SamGptCompletionsOptions) => {
  const { locale, t } = useI18n()
  const appChatStore = useAppChatStore()
  const { $sobreplanosDataService, $trackEvent, $config } = useNuxtApp()
  const route = useRoute()
  const user = ref<Customer | null>()
  let domainUserId: string
  let conversationId: string

  const messages = ref<AppMessage[]>([])
  const isLoading = ref(true)
  const showWritingFeedback = ref(false)
  const error = ref(false)
  const userStore = useUserStore()
  const messageQueueList = ref<{ id: string; hasReply: boolean }[]>([])
  const FEEDBACK_DISPLAY_DELAY_MS = 300
  let turnId: string
  let currentInteractionId: string

  const canSendMessage = computed<boolean>(() => {
    return messageQueueList.value.length < MAX_CONSECUTIVE_MESSAGES
  })

  const init = async () => {
    domainUserId = getDomainUserId()
    conversationId = getConversationId(listingIds, domainUserId)

    await setUser()

    handleStartConversation()
  }

  const setUser = () => {
    const data = userStore.user
    if (data?.name?.includes('Usuario sin')) data.name = ''

    user.value = data
  }

  const setHistoricalMessages = () => {
    const messageHistory = JSON.parse(localStorage?.getItem(conversationId)) || []
    messages.value = messageHistory
  }

  const handleStartConversation = async () => {
    setHistoricalMessages()
    isLoading.value = false

    if (!messages.value.length || appChatStore.endpointConfig.startFlow) getSamGreeting()

    if (appChatStore.contactFormConfig.userInitialMessage) {
      const initialMessage = appChatStore.contactFormConfig.userInitialMessage

      appChatStore.updateContactFormConfig({
        ...appChatStore.contactFormConfig,
        userInitialMessage: ''
      })
      await handleSendMessage({
        text: initialMessage
      })
    }
  }

  const getSamGreeting = () => {
    const mockGreetingMessages = appChatStore.chatGreeting

    if (mockGreetingMessages) {
      mockGreetingMessages.forEach((message: BotMessage) => addMessage('bot', message))
    }
    if (appChatStore.endpointConfig.shouldCallBotForGreeting) getSamResponse({ text: '' })
  }

  const addMessage = async (author: string, message: BotMessage | UserMessage) => {
    const newMessage = {
      author,
      timestamp: getTimeStamp(),
      ...message,
      text: parseMessage(message.text)
    } as AppMessage

    messages.value.push(newMessage)
    localStorage.setItem(conversationId, JSON.stringify(messages.value))
    localStorage.setItem(`sam_gpt_listing_${listingIds[0]}`, listingName)
    await updateSalesforceLead(user.value, domainUserId, conversationId, messages.value)
  }

  const handleRecommendationsAndMessages = async (listingIdsRecommendations, parsedMessages) => {
    const data = await fetchRecommendations(listingIdsRecommendations.join('~'), $sobreplanosDataService)
    addMessage('bot', {
      text: '',
      recommendations: data?.results
    })

    addMessage('bot', {
      text: t('samGpt.recommendations', {
        n: data?.results?.length
      })
    })
    parsedMessages.forEach((message) => {
      if (!message.list_picker) return
      message.text = transformBoldToMarkdown(message.text)
      addMessage('bot', message)
    }
    )
  }

  const enqueueUserMessage = (messageId: string): void => {
    messageQueueList.value.push({ id: messageId, hasReply: false })
    if (messageQueueList.value.length > 1) {
      messageQueueList.value = messageQueueList.value.map(item => ({
        ...item,
        hasReply: true
      }))
    }
  }

  const dequeueUserMessage = (messageId: string): void => {
    const index = messageQueueList.value.findIndex(item => item.id === messageId)

    if (index !== -1) {
      messageQueueList.value.splice(index, 1)
    }
  }

  const messageReplyStatus = (messageId: string): boolean => {
    return !!(messageQueueList.value.find(item => item.id === messageId)?.hasReply)
  }

  const getSamResponse = async (userMessage: UserMessage) => {
    showWritingFeedback.value = true
    const messageId = userMessage.id
    enqueueUserMessage(messageId)

    try {
      const response = await $fetch('/api/buyer-front/sam-gpt/send-completions-message', {
        method: 'POST',
        body: {
          locale: locale.value,
          conversationId,
          domainUserId,
          user: user.value,
          listingIds,
          enterpriseId,
          message: {
            id: messageId,
            text: userMessage.text
          },
          entryNameAlias: route.query.flag_sam_srp === 'true' ? 'RECOMMENDATIONS' : appChatStore.endpointConfig.entryNameAlias,
          startFlow: appChatStore.endpointConfig.startFlow,
          client: 'WEB',
          turnId: userMessage.turn_id,
          interactionId: userMessage.interaction_id
        },
        retry: 2
      }) as BotResponse

      currentInteractionId = response.output.metadata?.interaction_id

      const parsedSamMessages = parseSamMessageAndGetAdditionalMessages(response.output.messages, appChatStore)
      let parsedMessages = []

      const listingIdsRecommendations = extractListingIds(parsedSamMessages)
      parsedMessages = parsedSamMessages

      if (listingIdsRecommendations.length !== 0) {
        await handleRecommendationsAndMessages(listingIdsRecommendations, parsedMessages)
        return
      }

      if (shouldStopAfterScheduleMessage(response)) {
        addMessage('bot', { text: t('samGpt.messageAfterScheduling'), intent: 'AFTER_SCHEDULE' })
        appChatStore.updateHideChatInputField(true)
        return
      }
      const hasReplyMessage = messageReplyStatus(messageId)
      parsedMessages.forEach((message) => {
        addMessage('bot', { ...message, text: transformBoldToMarkdown(message.text), question: userMessage.text, hasReply: hasReplyMessage, id: messageId })
      })

      await $trackEvent({
        name: 'bot_agent_flow_tracked',
        version: '6-0-0',
        data: {
          user_id: user.value?.id,
          phone: user.value?.phone,
          listings: listingIds.map(listingId => ({ id: listingId })),
          conversation_id: conversationId,
          message_id: messageId,
          flow: {
            id: $config.public.twilioOrchestratorFlowId,
            name: appChatStore.endpointConfig.entryNameAlias,
            step: 'MESSAGE_DELIVERED',
            stage: 'QUESTIONS_AND_ANSWERS'
          },
          input_value: userMessage.text,
          turn_id: userMessage.turn_id,
          interaction_id: currentInteractionId
        }
      })
    } catch (error) {
      // @ts-ignore
      useLogger().error({
        message: 'Error requesting sam gpt completions endpoint',
        stack: error
      })

      await $trackEvent({
        name: 'bot_agent_flow_tracked',
        version: '6-0-0',
        data: {
          user_id: user.value?.id,
          phone: user.value?.phone,
          listings: listingIds.map(listingId => ({ id: listingId })),
          conversation_id: conversationId,
          message_id: messageId,
          flow: {
            id: $config.public.twilioOrchestratorFlowId,
            name: appChatStore.endpointConfig.entryNameAlias,
            step: 'BOT_LOST_RESPONSE',
            stage: 'QUESTIONS_AND_ANSWERS'
          },
          input_value: userMessage.text,
          turn_id: userMessage.turn_id,
          interaction_id: userMessage.interaction_id
        }
      })
    } finally {
      appChatStore.updateEndpointConfig({ ...appChatStore.endpointConfig, startFlow: false })

      dequeueUserMessage(messageId)

      showWritingFeedback.value = false
      await delay(FEEDBACK_DISPLAY_DELAY_MS)
      showWritingFeedback.value = messageQueueList.value.length > 0
    }
  }

  const persistUserMessage = (message: UserMessage) => {
    addMessage('user', message)
  }

  const handleSendMessage = async (message: UserMessage) => {
    if (!message?.text) return
    const messageId = generateMessageId(domainUserId)
    if (!turnId) turnId = generateTurnId()
    const interactionId = generateInteractionId()
    const payload = { ...message, id: messageId, turn_id: turnId, interaction_id: interactionId }
    persistUserMessage(payload)

    await getSamResponse(payload)
  }

  return {
    init,
    messages,
    addMessage,
    handleSendMessage,
    handleStartConversation,
    getSamGreeting,
    setUser,
    handleRecommendationsAndMessages,
    enqueueUserMessage,
    dequeueUserMessage,
    messageReplyStatus,
    showWritingFeedback,
    isLoading,
    error,
    canSendMessage,
    listingIds,
    messageQueueList,
    testData: {
      user
    }
  }
}
export default useSamGptCompletions
